import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarClock,
  CircleDollarSign,
  ClipboardList,
  Image as ImageIcon,
  MapPin,
  Phone,
  UserRound,
} from 'lucide-react'

import { assignmentsApi } from '@/services/assignments'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'

const statusStyles: Record<string, string> = {
  Upcoming: 'border-sky-200 bg-sky-50 text-sky-700',
  'In Progress': 'border-amber-200 bg-amber-50 text-amber-700',
  'On Hold': 'border-orange-200 bg-orange-50 text-orange-700',
  Completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Cancelled: 'border-rose-200 bg-rose-50 text-rose-700',
}

const ListSection = ({ title, items }: { title: string; items: string[] }) => (
  <Card className="rounded-2xl border-border/70 shadow-sm">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {items.length > 0 ? (
        <ul className="space-y-2 text-sm text-foreground">
          {items.map((item) => (
            <li key={item} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing listed yet.</p>
      )}
    </CardContent>
  </Card>
)

const EvidenceGrid = ({ title, images }: { title: string; images: string[] }) => (
  <Card className="rounded-2xl border-border/70 shadow-sm">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {images.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((imageUrl) => (
            <a
              key={imageUrl}
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
              className="group overflow-hidden rounded-xl border border-border/70 bg-muted/20"
            >
              <img
                src={imageUrl}
                alt={`${title} evidence`}
                className="h-28 w-full object-cover transition-transform group-hover:scale-[1.02]"
              />
            </a>
          ))}
        </div>
      ) : (
        <div className="flex h-28 items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/10 text-sm text-muted-foreground">
          <ImageIcon className="mr-2 h-4 w-4" />
          No photos yet
        </div>
      )}
    </CardContent>
  </Card>
)

export default function AssignmentJobViewPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const buildAssignmentsListPath = () => {
    const params = new URLSearchParams(location.search)
    params.delete('page')
    const query = params.toString()
    return withServiceInstanceId(`/assignments${query ? `?${query}` : ''}`, serviceInstanceId)
  }

  const jobViewQuery = useQuery({
    queryKey: ['assignment-job-view', id, serviceInstanceId],
    queryFn: () => assignmentsApi.getAssignmentJobView(id as string),
    enabled: Boolean(id),
  })

  if (jobViewQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    )
  }

  if (jobViewQuery.error || !jobViewQuery.data) {
    return (
      <Card className="mx-auto max-w-4xl rounded-2xl">
        <CardContent className="py-10">
          <Empty>
            <div className="text-center">
              <p className="text-lg font-medium">Job details unavailable</p>
              <p className="text-sm text-muted-foreground">
                {jobViewQuery.error ? getErrorMessage(jobViewQuery.error) : 'The assigned job view could not be loaded.'}
              </p>
              <Button className="mt-4" variant="outline" asChild>
                <Link to={buildAssignmentsListPath()}>Back to assignments</Link>
              </Button>
            </div>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  const jobView = jobViewQuery.data
  const payLabel = `${jobView.pay.currency}${Number(jobView.pay.amount || 0).toFixed(2)} / ${jobView.pay.type}`

  return (
    <div className="mx-auto max-w-4xl space-y-4 pb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(buildAssignmentsListPath())}
            title="Back to assignments"
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Assigned job view</p>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">{jobView.title}</h1>
          </div>
        </div>
        <Badge variant="outline" className={statusStyles[jobView.status] || 'border-slate-200 bg-slate-50 text-slate-700'}>
          {jobView.status}
        </Badge>
      </div>

      <Card className="overflow-hidden rounded-[28px] border-border/70 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex flex-wrap gap-2">
            {jobView.category.map((category) => (
              <Badge key={category} variant="secondary" className="rounded-full px-3 py-1">
                {category}
              </Badge>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-start gap-3">
                <BriefcaseBusiness className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Job ID</p>
                  <p className="font-medium text-foreground">{jobView.jobId}</p>
                </div>
              </div>
              {jobView.bookingId ? (
                <div className="flex items-start gap-3">
                  <ClipboardList className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Booking ID</p>
                    <p className="font-medium text-foreground">{jobView.bookingId}</p>
                  </div>
                </div>
              ) : null}
              <div className="flex items-start gap-3">
                <CalendarClock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Scheduled</p>
                  <p className="font-medium text-foreground">{jobView.scheduled || 'Not scheduled'}</p>
                  {jobView.startsIn ? <p className="text-sm text-muted-foreground">Starts in {jobView.startsIn}</p> : null}
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Location</p>
                  <p className="font-medium text-foreground">{jobView.location || 'Location not set'}</p>
                  {jobView.distanceKm != null ? (
                    <p className="text-sm text-muted-foreground">{jobView.distanceKm} km away</p>
                  ) : null}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CircleDollarSign className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Pay</p>
                  <p className="font-medium text-foreground">{payLabel}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Assigned to</p>
                  <p className="font-medium text-foreground">{jobView.assignedTo.name || 'Unassigned'}</p>
                  {jobView.assignedTo.phone ? (
                    <p className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      {jobView.assignedTo.phone}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Job description</p>
            <p className="mt-2 text-sm leading-6 text-foreground">{jobView.jobDescription || 'No description provided.'}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/70 p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Customer</p>
              <p className="mt-2 font-medium text-foreground">{jobView.customer.name || 'Not provided'}</p>
              {jobView.customer.phone ? <p className="text-sm text-muted-foreground">{jobView.customer.phone}</p> : null}
              {jobView.customer.address ? <p className="mt-2 text-sm text-muted-foreground">{jobView.customer.address}</p> : null}
            </div>
            <div className="rounded-2xl border border-border/70 p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Note</p>
              <p className="mt-2 text-sm leading-6 text-foreground">{jobView.note || 'No additional notes.'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <ListSection title="Includes" items={jobView.includes} />
        <ListSection title="Excludes" items={jobView.excludes} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <EvidenceGrid title="Before evidence" images={jobView.beforeEvidence} />
        <EvidenceGrid title="After evidence" images={jobView.afterEvidence} />
      </div>
    </div>
  )
}
